<script>
    import { get } from 'svelte/store';
    import t from '../../translations/translator.js';
	import { getAllGames } from '../stores/GameStore.js';
    import Section from "../controls/Section.svelte";
    import TabbedSection from "../controls/TabbedSection.svelte";
    import Label from "../controls/LabelSetting.svelte";
    import ButtonDefault from "../controls/ButtonDefault.svelte";
    import {userId} from "../stores/UserStore.js";
    import Password from "../controls/TextFieldPassword.svelte";
    import MsgBoxYesNo from '../msgboxes/MsgBoxYesNo.svelte';
    import GameSpecificSettings from '../controls/GameSpecificSettings.svelte';
    import Switch from '../controls/Switch.svelte';
    import { onMount, onDestroy, tick } from 'svelte';
    import TextField from '../controls/TextField.svelte';
    import { URL_OVERLAYS } from '../../lib/config.js';
    import { msgBox, showMsgBox, handlePositive, handleNegative } from "../stores/msgBox.js";
    import { Sections } from '../../lib/nav.js';

	export let id;
    export let onNavigate = (section, params = {}) => {};

    let activeTab = 'info';

    let userGame = null;
	let gameDescription = null;
	let isLoading = true;
	let error = null;
    let passwordRef;
    let copyButtonRef;
    let resetButtonRef;
    let deleteGameButtonRef;
    let previousValues = {};

	let confirmGameKeyVisible = false;
    let confirmGameKeyReset = false;

    let iframeDocsRef = {slot1: null,slot2: null,slot3: null,slot4: null,slot5: null,slot6: null,slot7: null,slot8: null};

    function handleRequestShowKey() {
		confirmGameKeyVisible = true;
	}

    function handleResetConfirmationOnYes() {
        confirmGameKeyReset = false;
        resetStreamKey();
    }

    function handleResetConfirmationOnNo() {
        confirmGameKeyReset = false;
    }

	function handleShowKeyOnYes() {
		passwordRef.show();
		confirmGameKeyVisible = false;
	}

	function handleShowKeyOnNo() {
		confirmGameKeyVisible = false;
	}

    function copyStreamKeyToClipboard() {

        if (!userGame?.gameKey) {
            copyButtonRef.showTemporaryText(t("copy_failed"), 3000);
            return;
        }

        navigator.clipboard.writeText(userGame.gameKey)
            .then(() => copyButtonRef.showTemporaryText(t("copied"), 3000))
            .catch(() => copyButtonRef.showTemporaryText(t("copy_failed"), 3000));

    }

    function requestGameKeyReset() {
        confirmGameKeyReset = true;
    }

    async function deleteGame() {
        const csrfToken = localStorage.getItem("csrfToken");
        try {
            let callType = "requestDeleteGame";
            const res = await fetch(`/rest/game/${get(userId)}/${userGame.id}/`, {
                method: "DELETE",
                body: JSON.stringify({callType}),
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": csrfToken
                }
            });
            const data = await res.json();
            if(data.error) {
                deleteGameButtonRef.showTemporaryText(t("delete_failed"))
                showMsgBox( t("GameSettings.msg.deletegame_failed_title"),
                            t("GameSettings.msg.deletegame_failed_body", data.error),
                            null,null);
            } else {
                deleteGameButtonRef.showTemporaryText(t("delete_success"))
                onNavigate(Sections.GAMES_OVERVIEW, {requireMenuRefresh: true})
            }
            
        } catch (err) {
            console.log("Error:", err);
            deleteGameButtonRef.showTemporaryText(t("delete_failed"))
        }
    }

    async function resetStreamKey() {
        const csrfToken = localStorage.getItem("csrfToken");
        try {
            let callType = "requestResetGameKey";
            const res = await fetch(`/rest/resetGameKey/${userGame.id}`, {
                method: "POST",
                body: JSON.stringify({callType}),
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": csrfToken
                }
            });
            const data = await res.json();
            userGame.gameKey = data.newGameKey;
            resetButtonRef.showTemporaryText(t("reset_succeeded"))
        } catch (err) {
            console.log("Error:", err);
            resetButtonRef.showTemporaryText(t("reset_failed"))
        }
        confirmGameKeyReset = false;
    }

    let saveTimeouts = {};

    function saveUserGameAttribute(key, value) {
        const csrfToken = localStorage.getItem("csrfToken");
        fetch(`https://www.deckblox.app/rest/game/${get(userId)}/${userGame.id}/attribute`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ key, value }),
            headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": csrfToken
                }
        });
        console.log('Autosaved: ' + key);
    }

    function autosave(key, options = { debounce: true }) {
        console.log('Autosaving: ' + key);
        if (isLoading) { 
            console.log('Autosaving stopped because still loading: ' + key);
            return; 
        }
        
        const currentValue = userGame[key];
        const previousValue = previousValues[key];

        if (JSON.stringify(currentValue) === JSON.stringify(previousValue)) {
            console.log('Autosaving stopped because not changed: ' + key);
            return;
        } 
        previousValues[key] = structuredClone(currentValue);

        const { debounce } = options;

        if (debounce) {
            clearTimeout(saveTimeouts[key]);
            saveTimeouts[key] = setTimeout(() => {
                saveUserGameAttribute(key, userGame[key]);
            }, 1000);
        } else {
            saveUserGameAttribute(key, userGame[key]);
        }
    }

    const handleDocsHeightMessage = (event) => {
        console.log("Incoming event: " + event.data);
        if (event.origin !== 'https://www.deckblox.com') return;
        const { type, height, slot } = event.data;
        console.log("Event data: " + type + ", " + height + ", " + slot);
        if (type === 'setHeight' && iframeDocsRef[slot]) {
            iframeDocsRef[slot].style.height = `${(height+100)}px`;
        }
    };

    $: tempConnType1 = userGame?.isConnectionDeckbloxAppEnabled;
    $: tempConnType1 !== undefined && autosave('isConnectionDeckbloxAppEnabled', { debounce: false });

    $: tempConnType2 = userGame?.isConnectionDirectTwitchEnabled;
    $: tempConnType2 !== undefined && autosave('isConnectionDirectTwitchEnabled', { debounce: false });

    $: tempConnType3 = userGame?.isConnectionHttpEnabled;
    $: tempConnType3 !== undefined && autosave('isConnectionHttpEnabled', { debounce: false });

    $: tempConnType4 = userGame?.isConnectionTouchPortalEnabled;
    $: tempConnType4 !== undefined && autosave('isConnectionTouchPortalEnabled', { debounce: false });

    $: tempConnType5 = userGame?.isConnectionStreamDeckEnabled;
    $: tempConnType5 !== undefined && autosave('isConnectionStreamDeckEnabled', { debounce: false });

    $: tempTheme = userGame?.theme;
    $: tempTheme !== undefined && autosave('theme', { debounce: false });

    $: tempTitle = userGame?.title;
    $: tempTitle !== undefined && autosave('title');

    $: tempSettings = userGame?.settings;
    $: tempSettings !== undefined && autosave('settings');

    $: tempState = userGame?.state;
    $: tempState !== undefined && autosave('state', { debounce: false });

    onDestroy(() => {
        for (const key in saveTimeouts) {
            clearTimeout(saveTimeouts[key]);
            saveUserGameAttribute(key, userGame[key]);
        }
        window.removeEventListener('message', handleDocsHeightMessage);
    });

	onMount(async () => {
		try {
			const csrfToken = localStorage.getItem("csrfToken");
			const res = await fetch(`https://www.deckblox.app/rest/game/${get(userId)}/${id}`, {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRF-Token': csrfToken
				}
			});
			userGame = await res.json();
            console.log('Getting game details (2):', userGame);
			const games = getAllGames();
			gameDescription = games.find(g => g.id === userGame.gameId);
		} catch (err) {
			error = err.message;
		} finally {
            console.log('Loading done');
            // We need to this ensure the triggers for 
            // userGame are handled before we say the loading is done.
            await tick(); 
            previousValues = structuredClone(userGame);
			isLoading = false;
		}

        window.addEventListener('message', handleDocsHeightMessage);

	});



</script>

{#if isLoading}
	<p class="text-gray-400 italic">Loading game settings...</p>
{:else if error}
	<p class="text-red-500">{error}</p>
{:else}
    <div class="space-y-6 p-4 text-left">
        <div class="flex items-center space-x-4">
            <img src={gameDescription.image} alt={gameDescription.name} class="w-32 h-32 object-cover rounded-lg" />
            <div>
                <h1 class="text-3xl font-bold">{userGame.title}</h1>
                <h2 class="text-1xl font-bold">{gameDescription.name}</h2>
            </div>
        </div>

		<div class="flex border-b border-gray-500 mb-4">
			{#each [
				{ key: 'info', label: 'Info' },
				{ key: 'settings', label: 'Settings' },
				{ key: 'theme', label: 'Theme' },
				/*{ key: 'gameKey', label: 'API & Keys' },*/
				{ key: 'controllers', label: 'Controllers' },
                { key: 'state', label: 'Manage' }
			] as tab}
				<button
					class="px-4 py-2 -mb-px font-semibold text-sm capitalize border-b-2 transition-colors duration-200"
					class:text-[var(--app-primary-color)]={activeTab === tab.key}
					class:border-transparent={activeTab !== tab.key}
					class:text-gray-500={activeTab !== tab.key}
                    style:border-bottom-color={activeTab === tab.key ? 'var(--app-background-color)' : 'transparent'}
					on:click={() => (activeTab = tab.key)}
				>
					{tab.label}
				</button>
			{/each}
		</div>

        {#if activeTab === 'info'}
            <Section 
                title="{t("GameSettings.label_game_details")}" 
                subtitle={gameDescription.description} >
            </Section>

            <Section 
                title="{t("GameSettings.label_overlay_details_title")}" 
                subtitle="{t("GameSettings.label_overlay_details_body")}"  >
                <TextField value="{URL_OVERLAYS}/game/{gameDescription.folderKey}/{userGame.id}/{userGame.gameKey}" class="w-full"/>
            </Section>
        {:else if activeTab === 'settings'}
            <Section 
                title="{t("GameSettings.label_game_name")}" 
                subtitle="{t("GameSettings.label_game_name_explanation")}"  >
                <TextField
                    placeholder="Game Title..."
                    bind:value={userGame.title}
                    autocomplete="off"
                    name="gameName"
                    class="w-full"
                />
            </Section>
            <GameSpecificSettings bind:settings={userGame.settings} {gameDescription} />
            
        {:else if activeTab === 'theme'}
            <Section 
                title="{t("GameSettings.label_game_theme")}" 
                subtitle="{t("GameSettings.label_game_theme_explanation")}"  >

                <select
                    class="section_select" 
                    bind:value={userGame.theme} >
                {#each gameDescription.themes as theme}
                    <option value={theme.id}>{theme.title}</option>
                {/each}
                </select>

            </Section>
        {:else if activeTab === 'controllers'}
            <Section 
                title="{t("GameSettings.label_game_key")}" 
                subtitle="{t("GameSettings.label_game_key_explanation")}" >

                <div class="space-y-4">
                    <div class="flex items-center space-x-4">
                        <Label text="{t("GameSettings.label_game_key_field")}"/>
                        <Password
                            placeholder="-"
                            showToggle={true}
                            value={userGame.gameKey}
                            autocomplete="new-password"
                            name="inputStreamKey"
                            bind:this={passwordRef}
                            onRequestShow={handleRequestShowKey}
                        />
                    </div>

                    <div class="flex items-center space-x-4 justify-end">
                        <ButtonDefault
                            text={t("copy")}
                            onClick={copyStreamKeyToClipboard}
                            bind:this={copyButtonRef}
                            />
                        <ButtonDefault
                            text={t("reset")}
                            onClick={requestGameKeyReset}
                            bind:this={resetButtonRef}
                            />
                    </div>
                </div>    
            </Section>

            <TabbedSection 
                title="{t("GameSettings.label_game_controllers")}" 
                subtitle="{t("GameSettings.label_game_controllers_explanation")}"
                tabs={[
                    { key: 'slot1', label: 'Deckblox App' },
                    { key: 'slot2', label: 'REST' },
                    { key: 'slot3', label: 'Touch Portal' }
                ]} >

                <svelte:fragment slot="slot1">
                    <iframe
                        title="docsFrameDeckbloxApp"
                        bind:this={iframeDocsRef.slot1}
                        src={`https://www.deckblox.com/gameDocs/${gameDescription.folderKey}/control-using-deckblox-app.php?theme=dashboard`}
                        class="w-full h-[600px] border-none"
                    />
                </svelte:fragment>

                <svelte:fragment slot="slot2">
                    <iframe
                        title="docsFrameRest"
                        bind:this={iframeDocsRef.slot2}
                        src={`https://www.deckblox.com/gameDocs/${gameDescription.folderKey}/control-using-rest.php?theme=dashboard`}
                        class="w-full h-[600px] border-none"
                    />
                </svelte:fragment>

                <svelte:fragment slot="slot3">
                    <iframe
                        title="docsFrameTouchPortal"
                        bind:this={iframeDocsRef.slot3}
                        src={`https://www.deckblox.com/gameDocs/${gameDescription.folderKey}/control-using-touch-portal.php?theme=dashboard`}
                        class="w-full h-[600px] border-none"
                    />
                </svelte:fragment>

            </TabbedSection>

         {:else if activeTab === 'state'}
            <Section 
                title="{t("GameSettings.label_game_state")}" 
                subtitle={t("GameSettings.label_game_state_description")} >
                <Switch bind:checked={userGame.state} />
            </Section>

            <Section 
                title="{t("GameSettings.label_game_remove_game")}" 
                subtitle={t("GameSettings.label_game_remove_game_description")} >
                 <ButtonDefault
                    text={t("GameSettings.label_game_remove_btn")}
                    onClick={() =>
                        showMsgBox(
                            t("GameSettings.msg.deletegame_confirmation_title"),
                            t("GameSettings.msg.deletegame_confirmation_body"),
                            () => { },
                            () => {
                                deleteGame();
                            }
                        )}
                    bind:this={deleteGameButtonRef}
                    />
            </Section>
        {/if}

    </div>
{/if}

<MsgBoxYesNo />

<!--{#if confirmGameKeyVisible}
    <MsgBoxYesNo
    title="{t("GameSettings.msg.show_game_key_title")}"
    message="{t("GameSettings.msg.show_game_key_body")}"
    onYes={handleShowKeyOnYes}
    onNo={handleShowKeyOnNo}
    />
{/if}

{#if confirmGameKeyReset}
    <MsgBoxYesNo
    title="{t("GameSettings.msg.reset_confirmation_title")}"
    message="{t("GameSettings.msg.reset_confirmation_body")}"
    onYes={handleResetConfirmationOnYes}
    onNo={handleResetConfirmationOnNo}
    />
{/if}

{#if confirmDeleteGame}
    <MsgBoxYesNo
    title="{t("GameSettings.msg.deletegame_confirmation_title")}"
    message="{t("GameSettings.msg.deletegame_confirmation_body")}"
    onYes={handleDeleteConfirmationOnYes}
    onNo={handleDeleteConfirmationOnNo}
    />
{/if}-->


